import { FiEdit } from 'react-icons/fi';

const advancedNoteTakingExercises = [
  {
    title: 'Band 8 Note Compression',
    type: 'fill-in',
    difficulty: 'Hard',
    bandLevel: 'Band 7.5-8.5',
    evaluationMode: 'ai-verify',
    aiReviewFocus: ['meaning preservation', 'use of abbreviations', 'efficient note form'],
    instructions:
      'Write a concise note version. Different but accurate shorthand can still be valid.',
    questions: [
      {
        text: 'Lecture note prompt: "The workshop has been moved from Room 12 to Room 18 because the original room was too small."',
        answer: 'Workshop → Rm 18 (not 12) bc room too small',
        acceptableAnswers: ['Workshop moved to room 18 because room 12 was too small'],
        evaluationMode: 'ai-verify',
        aiReviewFocus: ['captures correction', 'captures cause', 'keeps essential details only'],
        explanation:
          'A strong note captures the corrected room number and the reason without rewriting the full sentence.',
      },
      {
        text: 'Lecture note prompt: "Applications must be submitted by 5 p.m. on Thursday, but late submissions will only be accepted with medical evidence."',
        answer: 'Apps due Thu 5pm | late ok only w/ medical evidence',
        acceptableAnswers: ['Applications due Thursday 5pm; late ones need medical evidence'],
        evaluationMode: 'ai-verify',
        aiReviewFocus: ['deadline accuracy', 'exception handling', 'efficient shorthand'],
        explanation:
          'The deadline and the exception rule are the two essential details to keep in your notes.',
      },
    ],
  },
];

export const noteTakingData = {
  courseId: 'note-taking',
  courseType: 'listening-course',
  title: 'Note-Taking Skills',
  icon: FiEdit,
  overview: {
    impact: 'Essential for capturing key information',
    focus: 'Efficient note-taking while listening',
    uses: 'Sections 3 & 4 especially',
    duration: '20 minutes',
  },
  sections: [
    {
      title: 'Why Note-Taking Matters',
      content: `<p>In longer sections (3 & 4), you need to track multiple pieces of information. Good notes help you:</p>`,
      points: [
        'Keep track of main ideas and supporting details',
        'Remember information when filling gaps later',
        'Avoid confusion when speakers discuss multiple topics',
        'Stay focused and engaged with the audio',
      ],
    },
    {
      title: 'Abbreviation Systems',
      content: `<p>Develop a personal shorthand to write faster:</p>`,
      table: {
        headers: ['Full Word', 'Abbreviation', 'Category'],
        rows: [
          ['information', 'info', 'Common words'],
          ['because', 'bc / cuz', 'Connectors'],
          ['approximately', 'approx / ~', 'Modifiers'],
          ['government', 'govt', 'Institutions'],
          ['important', 'imp / !', 'Emphasis'],
          ['increase / decrease', '↑ / ↓', 'Symbols'],
          ['leads to / causes', '→', 'Relationships'],
          ['for example', 'e.g.', 'Examples'],
        ],
      },
    },
    {
      title: 'Note Structure Techniques',
      content: `<p>Organize your notes for quick reference:</p>`,
      points: [
        'Use indentation to show relationships',
        'Number points that come in sequence',
        'Circle or star key terms',
        'Draw arrows to show connections',
        'Leave space to add details later',
      ],
      examples: [
        { 
          label: 'Sample Notes', 
          text: 'Museum tour:\n  → starts 10am\n  → groups of 15 max\n  → * bring ID\n  → ends @ gift shop' 
        },
      ],
    },
    {
      title: 'What to Note Down',
      content: `<p>Focus on these key elements:</p>`,
      points: [
        'Names, dates, times, prices, quantities',
        'Main ideas and supporting reasons',
        'Changes, corrections, or updates to information',
        'Sequence and order of events',
        'Opinions and conclusions',
      ],
      warning: 'Don\'t try to write everything! Focus on answers and key details.',
    },
  ],
  exercises: [
    {
      title: 'Match Abbreviations',
      type: 'select',
      instructions: 'What does each abbreviation stand for?',
      questions: [
        {
          text: 'What does "→" mean in notes?',
          options: ['leads to / results in', 'equals', 'approximately'],
          answer: 'leads to / results in',
          explanation: 'Arrow shows cause-effect or sequence.',
        },
        {
          text: 'What does "↑" mean in notes?',
          options: ['increase', 'important', 'example'],
          answer: 'increase',
          explanation: 'Up arrow indicates increase or rise.',
        },
        {
          text: 'What does "e.g." mean?',
          options: ['for example', 'that is', 'in other words'],
          answer: 'for example',
          explanation: '"e.g." = exempli gratia = for example.',
        },
        {
          text: 'What does "~" mean in notes?',
          options: ['approximately', 'not equal to', 'similar to'],
          answer: 'approximately',
          explanation: 'Tilde (~) means "about" or "approximately."',
        },
      ],
    },
    {
      title: 'Create Abbreviations',
      type: 'fill-in',
      instructions: 'Create short abbreviations for these common words.',
      questions: [
        {
          text: 'Write an abbreviation for "university"',
          answer: 'uni',
          acceptableAnswers: ['univ'],
          evaluationMode: 'multi-answer',
          explanation: 'Keep recognizable first letters.',
        },
        {
          text: 'Write an abbreviation for "government"',
          answer: 'govt',
          acceptableAnswers: ['gov'],
          evaluationMode: 'multi-answer',
          explanation: 'Standard abbreviation for government.',
        },
        {
          text: 'Write an abbreviation for "information"',
          answer: 'info',
          acceptableAnswers: ['inf'],
          evaluationMode: 'multi-answer',
          explanation: 'Common, quick abbreviation.',
        },
      ],
    },
    {
      title: 'Choose What to Note',
      type: 'select',
      instructions: 'Which information should you prioritize noting down?',
      questions: [
        {
          text: 'In a conversation about booking a tour, what is MOST important to note?',
          options: ['Times, prices, dates', 'The guide\'s tone of voice', 'Greeting phrases'],
          answer: 'Times, prices, dates',
          explanation: 'Specific details are likely to be tested.',
        },
        {
          text: 'In a lecture about a scientific study, prioritize:',
          options: ['Numbers, findings, researcher names', 'Every word the lecturer says', 'Only the conclusion'],
          answer: 'Numbers, findings, researcher names',
          explanation: 'Key facts and figures are often tested.',
        },
        {
          text: 'When you hear a correction ("Actually, I meant 3pm, not 2pm"), you should:',
          options: ['Cross out and write the correction', 'Ignore it', 'Write both times'],
          answer: 'Cross out and write the correction',
          explanation: 'Corrections often contain the actual answer.',
        },
      ],
    },
    {
      title: 'Practice Quick Notes',
      type: 'writing',
      instructions: 'Write brief notes for this information.',
      questions: [
        {
          text: 'The library opens at nine-thirty on weekdays but eleven on weekends. It closes at eight every day.',
          example: 'Lib: wkday 9:30, wknd 11 | close 8pm daily',
          evaluationMode: 'ai-review',
          aiReviewFocus: ['shorthand accuracy', 'essential detail selection', 'clear note layout'],
          tip: 'Use symbols and abbreviations for times.',
        },
        {
          text: 'Students must register online before Friday. After registration, they should collect their ID from the main office.',
          example: 'Reg online < Fri → ID @ main office',
          evaluationMode: 'ai-review',
          aiReviewFocus: ['sequence', 'deadline capture', 'efficient symbols'],
          tip: 'Use arrows to show sequence.',
        },
        {
          text: 'The cost is approximately fifty dollars per person, but there is a twenty percent discount for groups of ten or more.',
          example: '~$50/person | 10+ = 20% off',
          evaluationMode: 'ai-review',
          aiReviewFocus: ['numbers', 'conditions', 'compact notation'],
          tip: 'Use symbols for approximate (≈ or ~) and percentages.',
        },
      ],
    },
    {
      title: 'Decode Notes',
      type: 'writing',
      instructions: 'What does each set of notes mean?',
      questions: [
        {
          text: 'Decode: "Lib → 3rd flr | open 9-6 | *bring ID"',
          example: 'The library is on the third floor, open from 9am to 6pm, and you must bring ID.',
          tip: 'Star (*) often marks important/required information.',
        },
        {
          text: 'Decode: "Museum tour: ↑price bc ↑demand | book early!"',
          example: 'Museum tour prices have increased because demand has increased. Book early!',
          tip: 'Arrows show direction of change.',
        },
      ],
    },
  ],
  tips: [
    'Develop your own consistent abbreviation system',
    'Practice note-taking with podcasts',
    'Review notes immediately after listening',
    'Don\'t sacrifice accuracy for speed—balance is key',
  ],
  dailyPractice: [
    'Take notes while watching English videos',
    'Practice converting full sentences to abbreviations',
    'Time yourself: aim for speed AND accuracy',
    'Review and refine your abbreviation system',
  ],
  backPath: '/exams/english/#listening',
  backLabel: 'Back to Listening',
  aiContext: 'Note-Taking Skills for IELTS Listening: Abbreviation systems, structural techniques, and strategies for capturing key information efficiently.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...noteTakingData,
      title: noteTakingData.title + ' (Foundations)',
      overview: {
        ...noteTakingData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: noteTakingData.sections.slice(0, Math.max(2, Math.ceil(noteTakingData.sections.length / 2))),
      exercises: noteTakingData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...noteTakingData,
      title: noteTakingData.title + ' (Advanced)',
      overview: {
        ...noteTakingData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: noteTakingData.sections,
      exercises: [
        ...noteTakingData.exercises.map(ex => ({
          ...ex,
          title: ex.title + ' (Advanced)',
        })),
        ...advancedNoteTakingExercises,
      ],
      tips: [
        ...(noteTakingData.tips || []),
        'For Band 8+, note corrections and exceptions immediately because these often hide the final answer.',
        'Advanced tip: reduce full clauses to symbols or compact chunks without losing the relationship between ideas.',
      ],
    };
  }
  return noteTakingData;
}
